import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Search, X } from "lucide-react";

// Fits the sheet to the visible viewport so the iPhone keyboard never covers Done.
function useVisualViewport() {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  const read = () => ({ top: vv ? vv.offsetTop : 0, height: vv ? vv.height : window.innerHeight });
  const [box, setBox] = useState(read);
  useEffect(() => {
    const on = () => setBox(read());
    (vv || window).addEventListener("resize", on);
    vv?.addEventListener("scroll", on);
    return () => { (vv || window).removeEventListener("resize", on); vv?.removeEventListener("scroll", on); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return box;
}

function useBodyLock() {
  useEffect(() => {
    const y = window.scrollY;
    const s = document.body.style;
    const prev = { position: s.position, top: s.top, width: s.width, overflow: s.overflow };
    Object.assign(s, { position: "fixed", top: `-${y}px`, width: "100%", overflow: "hidden" });
    return () => { Object.assign(s, prev); window.scrollTo(0, y); };
  }, []);
}

export function MobileMultiSheet({ id, title, options, value, onToggle, onClose }) {
  const [q, setQ] = useState("");
  const box = useVisualViewport();
  useBodyLock();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const blur = () => document.activeElement?.blur?.();
  const shown = options.filter(([, l]) => l.toLowerCase().includes(q.trim().toLowerCase()));
  return createPortal(
    <div data-testid={`${id}-sheet-overlay`} className="fixed inset-x-0 z-[100] flex flex-col justify-end bg-black/40" style={{ top: box.top, height: box.height }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby={`${id}-sheet-title`} data-testid={`${id}-sheet`}
        className="flex flex-col max-h-[calc(100%-1.5rem)] rounded-t-2xl bg-white shadow-2xl animate-in slide-in-from-bottom duration-200">
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-stone-200">
          <h2 id={`${id}-sheet-title`} className="font-semibold text-stone-900">{title}</h2>
          <span data-testid={`${id}-sheet-count`} className="text-sm text-stone-500">{value.length} selected</span>
        </div>
        <div className="px-4 py-2 border-b border-stone-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <input data-testid={`${id}-search`} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search disciplines" enterKeyHint="done"
              onKeyDown={(e) => e.key === "Enter" && blur()} className="w-full h-11 pl-9 pr-9 rounded-lg border border-stone-300 text-base outline-none focus:border-amber-800" />
            {q && <button type="button" aria-label="Clear search" data-testid={`${id}-search-clear`} onClick={() => setQ("")} className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-stone-400"><X className="h-4 w-4" /></button>}
          </div>
        </div>
        <ul data-testid={`${id}-sheet-list`} onTouchStart={blur} onScroll={blur} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-1" style={{ WebkitOverflowScrolling: "touch" }}>
          {shown.map(([k, l]) => {
            const on = value.includes(k);
            return (
              <li key={k}>
                <button type="button" role="checkbox" aria-checked={on} data-testid={`${id}-option-${k}`} onClick={() => onToggle(k)}
                  className={`w-full min-h-[48px] px-2 flex items-center gap-3 rounded-lg text-left text-base ${on ? "bg-amber-50 text-amber-900" : "text-stone-800"}`}>
                  <span className={`h-6 w-6 shrink-0 rounded border flex items-center justify-center ${on ? "bg-amber-800 border-amber-800 text-white" : "border-stone-300 bg-white"}`}>{on && <Check className="h-4 w-4" />}</span>
                  {l}
                </button>
              </li>
            );
          })}
          {!shown.length && <li className="px-2 py-4 text-sm text-stone-500">No match.</li>}
        </ul>
        <div className="border-t border-stone-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-white">
          <button type="button" data-testid={`${id}-done`} onClick={() => { blur(); onClose(); }}
            className="w-full h-12 rounded-full bg-amber-800 text-white font-semibold">Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
